// apps/merchant-v3/src/services/notificationsClient.ts
//
// Phase 6 Wave 12 Task 40 — Thin wrapper over `apiClient` for the
// merchant Notification Center surface (route `/notifications`) +
// the bell dropdown. All endpoints unwrap the canonical success
// envelope; the read endpoint falls back to an empty page on 404 /
// network failure so first paint never crashes (mirrors the wallet
// client pattern from Wave 9).
//
// Endpoints:
//   GET  /api/v1/notifications                  — paginated feed
//                                                 (cursor + filters)
//   POST /api/v1/notifications/:id/read         — mark a single row read
//   POST /api/v1/notifications/read-all         — bulk mark-read with
//                                                 optional filter scope
//
// (WS ticket minting is NOT here — see the note at the bottom of the file.)
//
// All three endpoints follow the canonical INTERFACE-STANDARDS envelope
// — `{ ok:true, data: ... }` on success, `{ ok:false, error: { ... } }`
// on failure — surfaced as `ApiError` from the api client.
//
// Decisions covered:
//   Bell + Center 6 tabs.
//   Center page surface + grouped list.
//   Live delivery via the WS channel
//               `merchant.${id}.notifications`.
//   Mark-read semantics (idempotent).
//   In-app sound preference (read by the Center page from
//               the existing Settings prefs — service stays agnostic).
//   Auth-ticket handshake — the FE POSTs here, attaches
//               the returned JWT as a Sec-WebSocket-Protocol header on
//               upgrade. Ticket TTL is short (≤60s); the wsClient
//               caches + re-fetches on every reconnect.
//
// vg-binding: PLAN:task-40
// vg-binding: P6.D-06
// vg-binding: P6.D-118
// vg-binding: P6.D-120
// vg-binding: P6.D-121
// vg-binding: P6.D-174
// vg-binding: P6.D-175
// vg-binding: G-65
// vg-binding: G-66
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import { apiClient, ApiError } from '@/lib/api/client';
import type {
  MarkAllReadResult,
  Notification,
  NotificationsListFilters,
  NotificationsListResult,
} from '@/types/notification';

const BASE = '/api/v1/notifications';

/** Build the query-string fragment from a filter snapshot. Empty / `'all'`
 *  filters are intentionally omitted so the BE log line stays narrow. */
function buildQuery(filters: NotificationsListFilters): string {
  const sp = new URLSearchParams();
  if (filters.category && filters.category !== 'all') {
    sp.set('category', filters.category);
  }
  // dbg-20260704 (notif unread filter) — the BE query contract is
  // `isRead=true|false`, not `read=unread|read`. The old `read=` param was
  // silently ignored, so the "unread only" toggle never filtered server-side.
  if (filters.read === 'unread') {
    sp.set('isRead', 'false');
  } else if (filters.read === 'read') {
    sp.set('isRead', 'true');
  }
  if (filters.cursor) sp.set('cursor', filters.cursor);
  if (typeof filters.page === 'number' && filters.page > 0) {
    sp.set('page', String(filters.page));
  }
  if (typeof filters.limit === 'number' && filters.limit > 0) {
    sp.set('limit', String(filters.limit));
  }
  const s = sp.toString();
  return s.length > 0 ? `?${s}` : '';
}

type NotificationsListWire =
  | NotificationsListResult
  | {
      notifications?: unknown;
      pagination?: {
        cursor?: unknown;
        nextCursor?: unknown;
      };
      unreadCount?: unknown;
      unread_count?: unknown;
      cursor?: unknown;
      nextCursor?: unknown;
    };

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function notificationArray(value: unknown): Notification[] {
  if (!Array.isArray(value)) return [];
  // BE serializer emits snake_case + Phase-3 field names; FE Notification
  // type uses camelCase. Translate per-row so consumers (Bell dropdown,
  // CenterPage row click) see the canonical shape.
  return (value as Array<Record<string, unknown>>).map((raw) => {
    const r = raw ?? {};
    const linkUrl =
      typeof r.actionUrl === 'string'
        ? (r.actionUrl as string)
        : typeof r.link_url === 'string'
          ? (r.link_url as string)
          : undefined;
    const ts =
      typeof r.ts === 'string'
        ? (r.ts as string)
        : typeof r.createdAt === 'string'
          ? (r.createdAt as string)
          : typeof r.created_at === 'string'
            ? (r.created_at as string)
            : new Date().toISOString();
    return {
      id: String(r.id ?? r._id ?? ''),
      category: (r.category as Notification['category']) ?? 'system',
      title: String(r.title ?? ''),
      body: String(r.body ?? ''),
      // i18n re-render support (dbg-20260704-110206-3372) — BE emits
      // snake_case i18n_key/i18n_params; keep null when absent (legacy rows).
      i18nKey:
        typeof r.i18n_key === 'string'
          ? r.i18n_key
          : typeof r.i18nKey === 'string'
            ? (r.i18nKey as string)
            : null,
      i18nParams:
        r.i18n_params && typeof r.i18n_params === 'object'
          ? (r.i18n_params as Record<string, string | number>)
          : r.i18nParams && typeof r.i18nParams === 'object'
            ? (r.i18nParams as Record<string, string | number>)
            : null,
      isRead: Boolean(r.isRead ?? r.is_read ?? false),
      ts,
      entityType: (r.entityType ?? r.entity_type) as Notification['entityType'],
      entityId: typeof r.entityId === 'string' ? r.entityId : typeof r.entity_id === 'string' ? r.entity_id : undefined,
      actionLabel: typeof r.actionLabel === 'string' ? r.actionLabel : undefined,
      actionUrl: linkUrl,
    };
  });
}

function normalizeNotificationsListResult(
  data: NotificationsListWire | null | undefined,
): NotificationsListResult {
  if (!data) return { rows: [], unreadCount: 0 };
  const wire = data as NotificationsListWire & {
    rows?: unknown;
    notifications?: unknown;
    unread_count?: unknown;
    nextCursor?: unknown;
  };
  const pagination =
    data && 'pagination' in data && data.pagination
      ? data.pagination
      : undefined;
  const rows = Array.isArray(wire.rows)
    ? notificationArray(wire.rows)
    : Array.isArray(wire.notifications)
      ? notificationArray(wire.notifications)
      : [];
  const unreadCount =
    typeof wire.unreadCount === 'number'
      ? wire.unreadCount
      : typeof wire.unread_count === 'number'
        ? wire.unread_count
        : undefined;
  const cursor =
    stringOrUndefined(wire.cursor) ??
    stringOrUndefined(wire.nextCursor) ??
    stringOrUndefined(pagination?.cursor) ??
    stringOrUndefined(pagination?.nextCursor);

  // BR-PAGING — pull total/page/totalPages/limit from BE pagination block.
  const pgBlock = pagination as
    | {
        total?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
        total_pages?: number;
      }
    | undefined;
  const total = typeof pgBlock?.total === 'number' ? pgBlock.total : undefined;
  const page = typeof pgBlock?.page === 'number' ? pgBlock.page : undefined;
  const limit = typeof pgBlock?.limit === 'number' ? pgBlock.limit : undefined;
  const pageCount =
    typeof pgBlock?.totalPages === 'number'
      ? pgBlock.totalPages
      : typeof pgBlock?.total_pages === 'number'
        ? pgBlock.total_pages
        : undefined;

  return {
    ...data,
    rows,
    unreadCount,
    cursor,
    ...(total !== undefined ? { total } : {}),
    ...(page !== undefined ? { page } : {}),
    ...(pageCount !== undefined ? { pageCount } : {}),
    ...(limit !== undefined ? { limit } : {}),
  };
}

/**
 * GET /api/v1/notifications — paginated feed of merchant notifications.
 * On 404 / network failure (e.g. brand-new merchant before any insert)
 * returns an empty page so the Center page first-paint stays clean.
 */
export async function listNotifications(
  filters: NotificationsListFilters = {},
): Promise<NotificationsListResult> {
  try {
    const data = await apiClient.get<NotificationsListWire>(
      `${BASE}${buildQuery(filters)}`,
    );
    return normalizeNotificationsListResult(data);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { rows: [], unreadCount: 0 };
    }
    throw err;
  }
}

/**
 * GET /api/v1/notifications/unread-count — total unread badge counter.
 * dbg-20260704 (notif badge) — the list endpoint does NOT return a
 * total-unread field, so the bell + Center header badge fell back to the
 * current page's unread count (e.g. 20 instead of the real 58) and the
 * topbar bell showed no badge at all. This dedicated endpoint returns the
 * merchant-wide unread total, independent of the active tab / page, so the
 * badge stays correct as the user paginates or switches tabs.
 * Returns 0 on 404 / network failure so first paint never crashes.
 */
export async function getUnreadCount(): Promise<number> {
  try {
    const data = await apiClient.get<{ unread?: unknown } | null>(
      `${BASE}/unread-count`,
    );
    const raw = (data ?? {}) as { unread?: unknown };
    return typeof raw.unread === 'number' ? raw.unread : 0;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return 0;
    throw err;
  }
}

/**
 * PATCH /api/v1/notifications/:id/read — flip a single row to read.
 * Idempotent — re-marking a read row returns `{ ok:true }` without
 * decrementing the unread counter. BE registers this route as PATCH
 * (`notification.routes.ts`), so an HTTP POST was silently 404'ing
 * and leaving the bell dropdown unread-state stale.
 */
export async function markRead(id: string): Promise<{ ok: true }> {
  return apiClient.patch<{ ok: true }>(
    `${BASE}/${encodeURIComponent(id)}/read`,
    {},
  );
}

/**
 * POST /api/v1/notifications/read-all — bulk mark-read. The optional
 * filter snapshot scopes the operation (e.g. only the active tab);
 * omit to mark every unread notification across every category.
 */
export async function markAllRead(
  filters?: Pick<NotificationsListFilters, 'category'>,
): Promise<MarkAllReadResult> {
  const body: Record<string, unknown> = {};
  if (filters?.category && filters.category !== 'all') {
    body.category = filters.category;
  }
  return apiClient.patch<MarkAllReadResult>(`${BASE}/read-all`, body);
}

// A `getWsTicket()` helper lived here, described as "the exported equivalent for
// tests + non-WS callers (e.g. a future Reconnect now button)". No caller ever
// arrived, and the live socket mints its own ticket inside `connectWs`
// (packages/ui/src/ws/wsClient.ts). Keeping a second exported way to POST
// /auth/ws-ticket only invited a future consumer to open a competing socket, so
// it was removed 2026-07-30. Add it back the day something needs it.
